import { createContext, ReactNode, useContext, useMemo } from "react";

import { show } from "@ebay/nice-modal-react";
import { useLocation } from "react-router-dom";

import { MarkerRubricKind } from "@/generated/graphql";
import { useRootNavigate } from "@/routes";

import { MarkerRubricModal } from "../components/marker-rubric-modal";

interface MarkerRubricLink {
  id: string;
  kind: MarkerRubricKind;
  location: { questionId: string | null };
}

interface Value {
  /** Marker rubrics associated with the assessment. */
  markerRubrics: Array<MarkerRubricLink>;
}

/**
 * Context providing student-readable MarkerRubrics in the Assessment.
 */
const AsssessmentRubricsContext = createContext<Value>({
  markerRubrics: [],
});

//////////////////////////////////////////////////////////////////////////////
// Provider                                                                 //
//////////////////////////////////////////////////////////////////////////////

interface ProviderProps extends Value {
  children: ReactNode;
}

/**
 * Provider for all loaded and visible links to an Assessment's MarkerRubrics.
 */
export function AssessmentRubricsProvider(props: ProviderProps) {
  const { children, ...rest } = props;

  return (
    <AsssessmentRubricsContext.Provider value={rest}>
      {children}
    </AsssessmentRubricsContext.Provider>
  );
}

//////////////////////////////////////////////////////////////////////////////
// Consumers                                                                //
//////////////////////////////////////////////////////////////////////////////

/**
 * Create a callback to navigte to an existing MarkerRubric matching search
 * criteria, if it exists and the student is allowed to see it.
 *
 * @param search
 * @param search.questionId Question ID to find linked MarkerRubric for
 * @param search.kind Kind of MarkerRubric to find.
 * @return Callback to open the linked MarkerRubric, or undefined if none found.
 */
export function useMarkerRubricOpen(search: {
  questionId?: string;
  kind?: MarkerRubricKind;
}): (() => void) | undefined {
  const value = useContext(AsssessmentRubricsContext);
  const location = useLocation();
  const rootNavigate = useRootNavigate();

  const { questionId, kind } = search;

  return useMemo(() => {
    const markerRubric = value.markerRubrics.find((r) => {
      if (questionId !== undefined) {
        return r.location.questionId === questionId;
      } else if (kind !== undefined) {
        return r.kind === kind;
      }
      return false;
    });

    if (markerRubric) {
      return () => {
        rootNavigate(`/rubrics/${markerRubric.id}`, {
          state: { returnPath: location.pathname + location.search },
        });
      };
    }
    return undefined;
  }, [value, location, rootNavigate, questionId, kind]);
}

/**
 * Create a callback to open an existing MarkerRubric in a modal, for contexts
 * where navigating away from the page is not possible (e.g. a live Socratic
 * attempt).
 */
export function useMarkerRubricOpenInModal(search: {
  questionId?: string;
  kind?: MarkerRubricKind;
}): (() => void) | undefined {
  const { questionId, kind } = search;

  const value = useContext(AsssessmentRubricsContext);

  return useMemo(() => {
    const markerRubric = value.markerRubrics.find((r) => {
      if (questionId !== undefined) {
        return r.location.questionId === questionId;
      } else if (kind !== undefined) {
        return r.kind === kind;
      }
      return false;
    });

    if (markerRubric) {
      return () => {
        show(MarkerRubricModal, { markerRubricId: markerRubric.id });
      };
    }
    return undefined;
  }, [value, questionId, kind]);
}
